import { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback, useId } from 'react';
import {
  Users, Copy, Check,
  Crown, Star, Briefcase, User, Eye,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { orgService } from '@core/supabase/services/orgService';
import { RoleChangeImpactModal } from './RoleChangeImpactModal';
import { PrivateStorageAvatar } from '@shared/components/ui/PrivateStorageAvatar';
import { OrgRole, OrgUser } from '@core/types';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { Modal } from '@shared/components/ui/Modal';
const BRAND = 'var(--ggj-accent)';
/** Leadership row accent (reference orange bar) — not used for junior/viewer */
const ACCENT_LEADERSHIP = '#f97316';

const ROLE_ORDER: OrgRole[] = ['owner', 'admin', 'senior_recruiter', 'junior_recruiter', 'viewer'];

const ROLE_META: Record<OrgRole, { label: string; short: string; icon: typeof Crown; color: string }> = {
  owner:            { label: 'Owner',           short: 'Owner',  icon: Crown,     color: '#f59e0b' },
  admin:            { label: 'Admin',            short: 'Admin',  icon: Star,      color: BRAND     },
  senior_recruiter: { label: 'Senior Recruiter', short: 'Senior', icon: Briefcase, color: '#22c55e' },
  junior_recruiter: { label: 'Junior Recruiter', short: 'Junior', icon: User,      color: '#3b82f6' },
  viewer:           { label: 'Viewer',           short: 'Viewer', icon: Eye,       color: '#94a3b8' },
};

export interface MemberWithStats extends OrgUser {
  stats: { applications: number; placed: number; candidates: number };
}

interface Connection { from: string; to: string }

function sortMembersStable(a: MemberWithStats, b: MemberWithStats): number {
  const na = (a.display_name ?? a.email).toLowerCase();
  const nb = (b.display_name ?? b.email).toLowerCase();
  return na.localeCompare(nb, undefined, { sensitivity: 'base' });
}

function buildHierarchyConnections(byR: Record<OrgRole, MemberWithStats[]>): Connection[] {
  const owners = [...byR.owner].sort(sortMembersStable);
  const admins = [...byR.admin].sort(sortMembersStable);
  const seniors = [...byR.senior_recruiter].sort(sortMembersStable);
  const juniors = [...byR.junior_recruiter].sort(sortMembersStable);
  const viewers = [...byR.viewer].sort(sortMembersStable);
  const out: Connection[] = [];

  admins.forEach((a, i) => {
    if (!owners.length) return;
    out.push({ from: owners[i % owners.length].id, to: a.id });
  });
  seniors.forEach((s, i) => {
    if (!owners.length) return;
    out.push({ from: owners[i % owners.length].id, to: s.id });
  });
  juniors.forEach((j, i) => {
    if (seniors.length) {
      out.push({ from: seniors[i % seniors.length].id, to: j.id });
    } else if (owners.length) {
      out.push({ from: owners[i % owners.length].id, to: j.id });
    } else if (admins.length) {
      out.push({ from: admins[i % admins.length].id, to: j.id });
    }
  });
  viewers.forEach((v, i) => {
    if (admins.length) {
      out.push({ from: admins[i % admins.length].id, to: v.id });
    } else if (seniors.length) {
      out.push({ from: seniors[i % seniors.length].id, to: v.id });
    } else if (owners.length) {
      out.push({ from: owners[i % owners.length].id, to: v.id });
    }
  });

  return out;
}

function childParentMap(connections: Connection[]): Record<string, string> {
  const m: Record<string, string> = {};
  connections.forEach(({ from, to }) => { m[to] = from; });
  return m;
}

/**
 * Reference layout: circle avatar left, name + detail right, thin vertical accent on far right.
 * `accentBarColor`: leadership uses orange; members use role color (never orange).
 */
function HierarchyCard({
  member,
  isSelected,
  isYou,
  onClick,
  nodeRef,
  accentBarColor,
}: {
  member: MemberWithStats;
  isSelected: boolean;
  isYou: boolean;
  onClick: () => void;
  nodeRef: (id: string, el: HTMLElement | null) => void;
  accentBarColor: string;
}) {
  const meta = ROLE_META[member.role];
  const [imgErr, setImgErr] = useState(false);
  useEffect(() => setImgErr(false), [member.avatar_url]);

  const initials = (member.display_name ?? member.email).split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const display = member.display_name ?? member.email.split('@')[0];

  return (
    <div
      ref={(el) => nodeRef(member.id, el)}
      className="org-hier-card-wrap w-full min-w-[220px] max-w-[280px] shrink-0"
    >
      <button
        type="button"
        onClick={onClick}
        data-role={member.role}
        aria-label={`${display}, ${meta.label}`}
        className="relative flex w-full items-stretch gap-0 overflow-hidden rounded-[10px] border bg-[var(--vscode-bg-elevated)] text-left shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--vscode-primary)_45%,transparent)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.2)]"
        style={{
          borderColor: isSelected ? meta.color : 'color-mix(in srgb, var(--vscode-border-primary) 88%, transparent)',
          boxShadow: isSelected ? `0 0 0 2px ${meta.color}35` : undefined,
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-3 pr-2">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full ggj-text-meta font-bold sm:h-11 sm:w-11 sm:ggj-text-body"
            style={{
              backgroundColor: `color-mix(in srgb, ${meta.color} 18%, var(--vscode-bg-tertiary))`,
              color: meta.color,
              border: `2px solid color-mix(in srgb, ${meta.color} 40%, var(--vscode-border-primary))`,
            }}
          >
            {member.avatar_url && !imgErr ? (
              <PrivateStorageAvatar
                path={member.avatar_url}
                className="h-full w-full object-cover"
                fallback={<span>{initials}</span>}
                onImageError={() => setImgErr(true)}
                onSignFailed={() => setImgErr(true)}
              />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate ggj-text-body font-bold leading-tight vscode-text-primary">
              {display}
              {isYou && <span className="font-normal vscode-text-tertiary"> · you</span>}
            </p>
            <p className="truncate ggj-text-meta leading-snug vscode-text-tertiary">{meta.label}</p>
          </div>
        </div>
        <div className="w-[3px] shrink-0 self-stretch" style={{ backgroundColor: accentBarColor }} aria-hidden />
      </button>
    </div>
  );
}

export interface OrgHierarchyPageProps {
  onSelectMember?: (member: MemberWithStats | null) => void;
  /** Controlled from Settings sidebar — Add member modal */
  addMemberModalOpen?: boolean;
  onAddMemberModalOpenChange?: (open: boolean) => void;
}

export function OrgHierarchyPage({
  onSelectMember,
  addMemberModalOpen = false,
  onAddMemberModalOpenChange,
}: OrgHierarchyPageProps) {
  const { user } = useAuth();
  const perms = usePermissions();

  const [members, setMembers]     = useState<MemberWithStats[]>([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteForm, setInviteForm] = useState({ full_name: '', email: '', role: 'junior_recruiter' as OrgRole });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [roleModalMember, setRoleModalMember] = useState<MemberWithStats | null>(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleFeedback, setRoleFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRefs     = useRef<Record<string, HTMLElement>>({});
  const [pathItems, setPathItems] = useState<{ d: string; arrow?: boolean }[]>([]);
  const arrowMarkerId = useId().replace(/:/g, '');

  const updateConnectorPaths = useCallback(() => {
    const container = containerRef.current;
    if (!container || !members.length) {
      setPathItems([]);
      return;
    }
    const rect = container.getBoundingClientRect();
    const byR = ROLE_ORDER.reduce((acc, r) => {
      acc[r] = members.filter((m) => m.role === r);
      return acc;
    }, {} as Record<OrgRole, MemberWithStats[]>);

    const connections = buildHierarchyConnections(byR);
    const byParent = new Map<string, string[]>();
    connections.forEach(({ from, to }) => {
      if (!byParent.has(from)) byParent.set(from, []);
      byParent.get(from)!.push(to);
    });

    const items: { d: string; arrow?: boolean }[] = [];

    byParent.forEach((childIds, parentId) => {
      const fe = nodeRefs.current[parentId];
      const childEls = childIds.map((id) => nodeRefs.current[id]).filter(Boolean) as HTMLElement[];
      if (!fe || !childEls.length) return;

      const fr = fe.getBoundingClientRect();
      const px = fr.left - rect.left + fr.width / 2;
      const pyBottom = fr.top - rect.top + fr.height;

      const childRects = childEls.map((te) => {
        const r = te.getBoundingClientRect();
        return {
          cx: r.left - rect.left + r.width / 2,
          yTop: r.top - rect.top,
        };
      });

      const minChildTop = Math.min(...childRects.map((c) => c.yTop));
      const yBus = pyBottom + Math.max(14, Math.min(56, (minChildTop - pyBottom) * 0.42));
      const ySafe = Math.min(yBus, minChildTop - 6);

      if (childRects.length === 1) {
        const { cx, yTop } = childRects[0];
        items.push({
          d: `M ${px} ${pyBottom} L ${px} ${ySafe} L ${cx} ${ySafe} L ${cx} ${yTop}`,
          arrow: true,
        });
        return;
      }

      const xs = childRects.map((c) => c.cx);
      const xMin = Math.min(...xs);
      const xMax = Math.max(...xs);
      const hLeft = Math.min(px, xMin);
      const hRight = Math.max(px, xMax);

      items.push({ d: `M ${px} ${pyBottom} L ${px} ${ySafe}` });
      items.push({ d: `M ${hLeft} ${ySafe} L ${hRight} ${ySafe}` });
      childRects.forEach(({ cx, yTop }) => {
        items.push({ d: `M ${cx} ${ySafe} L ${cx} ${yTop}`, arrow: true });
      });
    });

    setPathItems(items);
  }, [members]);

  useLayoutEffect(() => {
    updateConnectorPaths();
    const id = requestAnimationFrame(() => updateConnectorPaths());
    return () => cancelAnimationFrame(id);
  }, [updateConnectorPaths, selectedId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => updateConnectorPaths());
    ro.observe(el);
    window.addEventListener('resize', updateConnectorPaths);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateConnectorPaths);
    };
  }, [updateConnectorPaths]);

  useEffect(() => {
    if (!user?.orgId) return;
    Promise.all([
      orgService.getTeamMembers(user.orgId),
      orgService.getTeamStats(user.orgId),
    ]).then(([teamMembers, stats]) => {
      setMembers(teamMembers.map((m) => ({
        ...m,
        stats: {
          applications: stats.byUserId[m.id]?.applications ?? 0,
          placed:        stats.byUserId[m.id]?.placed       ?? 0,
          candidates:    stats.byKeycloakSub[m.keycloak_sub]?.candidates ?? 0,
        },
      })));
    }).finally(() => setLoading(false));
  }, [user?.orgId]);

  const nodeRef = (id: string, el: HTMLElement | null) => {
    if (el) nodeRefs.current[id] = el;
    else delete nodeRefs.current[id];
  };

  const handleSelectMember = (m: MemberWithStats) => {
    const next = selectedId === m.id ? null : m.id;
    setSelectedId(next);
    onSelectMember?.(next ? m : null);
  };

  const applyRoleChange = async (toRole: OrgRole) => {
    if (!user?.orgId || !roleModalMember) return;
    setRoleSaving(true);
    setRoleFeedback(null);
    try {
      await orgService.updateMemberRole(user.orgId, roleModalMember.id, toRole);
      setMembers((prev) => prev.map((m) => (m.id === roleModalMember.id ? { ...m, role: toRole } : m)));
      if (selectedId === roleModalMember.id) {
        onSelectMember?.({ ...roleModalMember, role: toRole });
      }
      setRoleModalMember(null);
      setRoleFeedback({ kind: 'ok', text: 'Role updated.' });
      setTimeout(() => setRoleFeedback(null), 4000);
    } catch (err) {
      setRoleFeedback({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Could not update role.',
      });
    } finally {
      setRoleSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!user?.orgId || !inviteForm.full_name.trim() || !inviteForm.email.trim()) return;
    setInviteSaving(true); setInviteError(null);
    try {
      const newMember = await orgService.addMember(user.orgId, {
        full_name: inviteForm.full_name.trim(),
        email:     inviteForm.email.trim(),
        role:      inviteForm.role,
      });
      setMembers((prev) => [...prev, { ...newMember, stats: { applications: 0, placed: 0, candidates: 0 } }]);
      setInviteForm({ full_name: '', email: '', role: 'junior_recruiter' });
      onAddMemberModalOpenChange?.(false);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setInviteSaving(false);
    }
  };

  const byRole = ROLE_ORDER.reduce((acc, r) => {
    acc[r] = members.filter((m) => m.role === r); return acc;
  }, {} as Record<OrgRole, MemberWithStats[]>);

  const connections = useMemo(() => {
    const byR = ROLE_ORDER.reduce((acc, r) => {
      acc[r] = members.filter((m) => m.role === r);
      return acc;
    }, {} as Record<OrgRole, MemberWithStats[]>);
    return buildHierarchyConnections(byR);
  }, [members]);

  const parentOf = useMemo(() => childParentMap(connections), [connections]);

  const membersByParent = useMemo(() => {
    const g: Record<string, MemberWithStats[]> = {};
    members.forEach((m) => {
      if (m.role !== 'junior_recruiter' && m.role !== 'viewer') return;
      const p = parentOf[m.id];
      if (!p) return;
      if (!g[p]) g[p] = [];
      g[p].push(m);
    });
    Object.keys(g).forEach((k) => g[k].sort(sortMembersStable));
    return g;
  }, [members, parentOf]);

  const ownersSorted   = useMemo(() => [...byRole.owner].sort(sortMembersStable), [byRole.owner]);
  const coLeadersSorted = useMemo(
    () => [...byRole.admin, ...byRole.senior_recruiter].sort(sortMembersStable),
    [byRole.admin, byRole.senior_recruiter],
  );

  /** Column parents: owners with direct member children first, then co-leaders with children */
  const columnParents = useMemo(() => {
    const out: MemberWithStats[] = [];
    const seen = new Set<string>();
    ownersSorted.forEach((o) => {
      if ((membersByParent[o.id]?.length ?? 0) > 0 && !seen.has(o.id)) {
        out.push(o); seen.add(o.id);
      }
    });
    coLeadersSorted.forEach((c) => {
      if ((membersByParent[c.id]?.length ?? 0) > 0 && !seen.has(c.id)) {
        out.push(c); seen.add(c.id);
      }
    });
    return out;
  }, [ownersSorted, coLeadersSorted, membersByParent]);

  return (
    <div className="space-y-4">

      {loading ? (
        <FullPageSpinner />
      ) : members.length === 0 ? (
        <EmptyState icon={Users} title="No team members yet" description="Use Add member in the sidebar when you have permission." />
      ) : (
        <div className="ggj-param-group overflow-hidden">
          <div className="ggj-profile-section-h flex flex-row items-center justify-between gap-2">
            <span>Org Chart</span>
            <span className="ggj-text-meta vscode-text-tertiary font-normal tabular-nums">{members.length}</span>
          </div>

          <div
            ref={containerRef}
            className="relative overflow-x-auto rounded-b-xl px-4 py-10 sm:px-8"
            style={{
              minWidth: 560,
              minHeight: 280,
              backgroundColor: 'color-mix(in srgb, var(--vscode-bg-tertiary) 55%, var(--vscode-bg-primary))',
            }}
          >
            <svg className="pointer-events-none absolute inset-0 z-0 h-full w-full" aria-hidden style={{ top: 0, left: 0 }}>
              <defs>
                <marker
                  id={arrowMarkerId}
                  markerWidth="7"
                  markerHeight="7"
                  refX="6"
                  refY="3.5"
                  orient="auto"
                  markerUnits="userSpaceOnUse"
                >
                  <path d="M 0 0 L 7 3.5 L 0 7 Z" fill="#525252" />
                </marker>
              </defs>
              {pathItems.map(({ d, arrow }, i) => (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  stroke="color-mix(in srgb, var(--vscode-text-primary) 38%, var(--vscode-border-primary))"
                  strokeOpacity={0.92}
                  strokeWidth={1.15}
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                  markerEnd={arrow ? `url(#${arrowMarkerId})` : undefined}
                />
              ))}
            </svg>

            <div className="relative z-[1] mx-auto flex max-w-[1100px] flex-col items-center gap-10">
              {/* Level 1 — Owner / Leader */}
              {ownersSorted.length > 0 && (
                <div className="flex flex-wrap items-start justify-center gap-6">
                  {ownersSorted.map((m) => (
                    <HierarchyCard
                      key={m.id}
                      member={m}
                      isSelected={selectedId === m.id}
                      isYou={m.keycloak_sub === user?.id}
                      onClick={() => handleSelectMember(m)}
                      nodeRef={nodeRef}
                      accentBarColor={ACCENT_LEADERSHIP}
                    />
                  ))}
                </div>
              )}

              {/* Level 2 — Co-leaders (Admin + Senior) */}
              {coLeadersSorted.length > 0 && (
                <div className="flex flex-wrap items-start justify-center gap-6">
                  {coLeadersSorted.map((m) => (
                    <HierarchyCard
                      key={m.id}
                      member={m}
                      isSelected={selectedId === m.id}
                      isYou={m.keycloak_sub === user?.id}
                      onClick={() => handleSelectMember(m)}
                      nodeRef={nodeRef}
                      accentBarColor={ACCENT_LEADERSHIP}
                    />
                  ))}
                </div>
              )}

              {/* Level 3 — Members (Junior + Viewer) in columns under each parent */}
              {columnParents.length > 0 && (
                <div className="flex w-full flex-wrap items-start justify-center gap-8 lg:gap-10">
                  {columnParents.map((parent) => {
                    const kids = membersByParent[parent.id] ?? [];
                    if (!kids.length) return null;
                    const pName = parent.display_name ?? parent.email.split('@')[0];
                    return (
                      <div key={parent.id} className="flex w-full max-w-[580px] flex-col items-center gap-3">
                        <p className="text-center text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                          Under {pName}
                        </p>
                        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                          {kids.map((m) => {
                            const meta = ROLE_META[m.role];
                            return (
                              <HierarchyCard
                                key={m.id}
                                member={m}
                                isSelected={selectedId === m.id}
                                isYou={m.keycloak_sub === user?.id}
                                onClick={() => handleSelectMember(m)}
                                nodeRef={nodeRef}
                                accentBarColor={meta.color}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 border-t vscode-border px-5 py-3">
            {ROLE_ORDER.map((role) => {
              const meta = ROLE_META[role];
              const Icon = meta.icon;
              return (
                <span key={role} className="flex items-center gap-1.5 ggj-text-meta vscode-text-secondary">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: meta.color }} />
                  <Icon className="h-3 w-3 vscode-text-tertiary" />
                  {meta.label} ({byRole[role].length})
                </span>
              );
            })}
          </div>
        </div>
      )}

      {!loading && members.length > 0 && selectedId && perms.canChangeRoles && (() => {
        const m = members.find((x) => x.id === selectedId);
        if (!m) return null;
        return (
          <div className="ggj-param-group overflow-hidden">
            <div className="ggj-profile-section-h">Member role</div>
            <div className="ggj-profile-section-body space-y-3">
              <p className="ggj-text-meta vscode-text-secondary">
                Current role: <span className="font-medium vscode-text-primary">{ROLE_META[m.role].label}</span>
                {m.keycloak_sub === user?.id ? (
                  <span className="vscode-text-tertiary"> · you</span>
                ) : null}
              </p>
              <button
                type="button"
                className="btn-secondary ggj-text-body"
                onClick={() => setRoleModalMember(m)}
              >
                Change role…
              </button>
              {roleFeedback ? (
                <p className={`ggj-text-meta ${roleFeedback.kind === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {roleFeedback.text}
                </p>
              ) : null}
            </div>
          </div>
        );
      })()}

      <RoleChangeImpactModal
        open={!!roleModalMember}
        onClose={() => !roleSaving && setRoleModalMember(null)}
        memberName={(roleModalMember?.display_name ?? roleModalMember?.email ?? 'Member').trim()}
        fromRole={roleModalMember?.role ?? 'viewer'}
        onConfirm={(to) => void applyRoleChange(to)}
        saving={roleSaving}
      />

      {perms.canManageTeam && (
        <Modal
          open={addMemberModalOpen}
          onClose={() => { onAddMemberModalOpenChange?.(false); setInviteError(null); }}
          title="Add Team Member"
          subtitle="Creates a pending org row — user needs a matching Keycloak account to sign in."
          icon={<UserPlus className="h-4 w-4 text-white" />}
          size="md"
        >
          <div className="-mx-1 space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-1">
              <div>
                <label className="mb-1 block ggj-text-meta vscode-text-tertiary">Full name</label>
                <input
                  className="input w-full"
                  placeholder="e.g. Priya Sharma"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm((f) => ({ ...f, full_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block ggj-text-meta vscode-text-tertiary">Work email</label>
                <input
                  className="input w-full"
                  type="email"
                  placeholder="priya@company.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block ggj-text-meta vscode-text-tertiary">Role</label>
                <select
                  className="input w-full"
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value as OrgRole }))}
                >
                  {ROLE_ORDER.filter((r) => r !== 'owner').map((r) => (
                    <option key={r} value={r}>{ROLE_META[r].label}</option>
                  ))}
                </select>
              </div>
            </div>
            {inviteError && <p className="ggj-text-meta text-red-400">{inviteError}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="btn-secondary ggj-text-body"
                onClick={() => { onAddMemberModalOpenChange?.(false); setInviteError(null); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary ggj-text-body"
                disabled={inviteSaving || !inviteForm.full_name.trim() || !inviteForm.email.trim()}
                onClick={() => void handleAddMember()}
              >
                {inviteSaving ? 'Adding…' : 'Add member'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
